import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { renderedTextLength } from '@design-system/components/markdownEditor/mdEditable';
import { useAddPost } from '../api/useAddPost';
import { useUpdateDraft } from '../api/useUpdateDraft';
import { CommunityId } from '../model/community.types';
import { MAX_POST_CHARS } from '../model/newPost.constants';
import { PostId, PostMediaItem } from '../model/post.types';
import { analytics } from './analytics';
import { containsCommunityMention } from './channelMention';
import { uploadMediaInputs } from './mediaInputs';
import { resolveInfluencerId } from './resolveInfluencerId';
import { DraftVideo, isAudioFile, useMediaDraft } from './useMediaDraft';

export { isAudioFile };
export type { DraftVideo };

// An existing draft reopened in the composer. Its already-uploaded media is
// kept as-is unless the creator removes an item, so the update resends it.
export type EditedDraft = {
  id: PostId;
  text: string;
  media: PostMediaItem[];
  onSaved: () => void;
};

type NewPost = {
  text: string;
  setText: (text: string) => void;
  files: File[];
  videos: DraftVideo[];
  keptMedia: PostMediaItem[];
  removeKeptMedia: (item: PostMediaItem) => void;
  mediaCount: number;
  addFiles: (files: File[]) => void;
  removeFile: (file: File) => void;
  addVideo: (uuid: string, preview?: Omit<DraftVideo, 'uuid'>) => void;
  removeVideo: (uuid: string) => void;
  canSubmit: boolean;
  isSubmitting: boolean;
  isEditing: boolean;
  submit: () => Promise<void>;
  saveDraft: () => Promise<void>;
  schedule: (when: Date) => Promise<void>;
};

export const useNewPost = (
  communityId: CommunityId,
  influencerId?: string,
  editedDraft?: EditedDraft,
): NewPost => {
  const addPost = useAddPost(communityId, influencerId);
  const [updateDraft] = useUpdateDraft();
  const [text, setText] = useState(editedDraft?.text ?? '');
  const [keptMedia, setKeptMedia] = useState<PostMediaItem[]>(
    editedDraft?.media ?? [],
  );
  const media = useMediaDraft(keptMedia.length);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // isSubmitting drives the UI but lands a render later, so two clicks in one
  // tick would both pass it. The ref is what actually blocks the second send.
  const sending = useRef(false);

  const { files, videos, mediaCount, clear } = media;

  const removeKeptMedia = useCallback((item: PostMediaItem) => {
    setKeptMedia((current) => current.filter((kept) => kept !== item));
  }, []);

  const canSubmit = (text.trim().length > 0 || mediaCount > 0) && !isSubmitting;

  // Tri-state: absent posts now, null saves a draft, a date schedules it.
  const create = useCallback(
    async (publishAt?: Date | null) => {
      if (sending.current) return;
      if (renderedTextLength(text) > MAX_POST_CHARS) {
        toast.error("Posts can't be longer than 20K characters.");
        return;
      }
      sending.current = true;
      const postsNow = publishAt === undefined;

      setIsSubmitting(true);
      // Stored verbatim on purpose: escaping here double-encodes "&" into
      // "&amp;" for every plain-text reader (mobile). Escaping belongs to
      // mdToHTML at render.
      const result = await addPost({
        text,
        files,
        videoUuids: videos.map((video) => video.uuid),
        ...(postsNow ? {} : { publishAt }),
      });
      if (result.type === 'success') {
        if (postsNow) {
          analytics.trackPostCreated(files, videos.length);
          if (containsCommunityMention(text)) {
            analytics.trackBroadcastSent();
          }
        }
        toast.success(
          postsNow
            ? 'Post added'
            : publishAt === null
            ? 'Draft saved'
            : 'Post scheduled',
        );
        setText('');
        clear();
      } else if (postsNow) {
        analytics.trackPostFailed(files, videos.length, result.reason);
      }
      sending.current = false;
      setIsSubmitting(false);
    },
    [addPost, files, videos, text, clear],
  );

  const saveEditedDraft = useCallback(async () => {
    if (!editedDraft || sending.current) return;
    if (renderedTextLength(text) > MAX_POST_CHARS) {
      toast.error("Posts can't be longer than 20K characters.");
      return;
    }

    sending.current = true;
    setIsSubmitting(true);
    try {
      const creatorId = resolveInfluencerId(influencerId);
      let uploaded: PostMediaItem['input'][] = [];
      if (files.length > 0 || videos.length > 0) {
        if (creatorId === null) throw new Error('Missing creator id');
        uploaded = await uploadMediaInputs(
          files,
          videos.map((video) => video.uuid),
          creatorId,
        );
      }
      const { data } = await updateDraft({
        variables: {
          communityId,
          postId: editedDraft.id,
          text,
          media: [...keptMedia.map((item) => item.input), ...uploaded],
        },
      });
      if (data?.updatePost) {
        clear();
        editedDraft.onSaved();
      }
    } catch {
      toast.error('Something went wrong');
    } finally {
      sending.current = false;
      setIsSubmitting(false);
    }
  }, [
    editedDraft,
    text,
    files,
    videos,
    keptMedia,
    clear,
    updateDraft,
    communityId,
    influencerId,
  ]);

  return {
    text,
    setText,
    files,
    videos,
    keptMedia,
    removeKeptMedia,
    mediaCount,
    addFiles: media.addFiles,
    removeFile: media.removeFile,
    addVideo: media.addVideo,
    removeVideo: media.removeVideo,
    canSubmit,
    isSubmitting,
    isEditing: editedDraft !== undefined,
    submit: editedDraft ? saveEditedDraft : () => create(),
    saveDraft: () => create(null),
    schedule: (when: Date) => create(when),
  };
};
